import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { MessagingChannelType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EmptyStateMessage } from '~components/Common/EmptyStateMessage';
import MessagingChannelRowActions, {
  ROW_DETAIL_REPLACED_BY_ACTIONS,
} from '~components/Messaging/MessagingChannelRowActions';
import MutedConversationIcon from '~components/Messaging/MutedConversationIcon';
import UnreadCountBadge, {
  UNREAD_EMPHASIS_FONT_WEIGHT,
} from '~components/Messaging/UnreadCountBadge';
import { Skeleton } from '~components/Skeleton/Skeleton';
import { MessagingChannel } from '~hooks/useMessaging/models';
import useMessagingChannelRowTypers from '~hooks/useMessaging/useMessagingChannelRowTypers';
import MessagingArchivedTag from '~pages/Messages/components/MessagingArchivedTag';
import {
  MESSAGING_AVATAR_SIZE_IN_PX,
  MessagingChannelAvatar,
} from '~pages/Messages/components/MessagingAvatar';
import MessagingLoadError from '~pages/Messages/components/MessagingLoadError';
import MessagingTag from '~pages/Messages/components/MessagingTag';
import { messagingTypingLabel } from '~pages/Messages/components/MessagingTypingIndicator';
import { BROADCAST_ACCENT_WIDTH_IN_PX } from '~pages/Messages/messagingBroadcastAccent';
import {
  messagingChannelTags,
  messagingChannelTitle,
} from '~pages/Messages/messagingChannelLabels';
import {
  messagingChannelPreview,
  messagingLastActivityLabel,
} from '~pages/Messages/messagingChannelPreview';
import { useMessagingDraft } from '~pages/Messages/MessagingDraftContext';
import { MESSAGING_LABEL_FONT_WEIGHT } from '~pages/Messages/messagingTypography';
import { Nullable } from '~types/Nullable';
import useInfiniteScroll from '~utils/hooks/useInfiniteScroll';
import useOnScreenRowIds from '~utils/hooks/useOnScreenRowIds';

const SKELETON_ROW_COUNT = 6;

const UNREAD_DOT_SIZE_IN_PX = 8;

// Sits in the row's left padding so read and unread rows stay aligned.
const UNREAD_DOT_LEFT_IN_PX = 4;

const ROW_PADDING_Y = 1.25;

// Centers the 28px actions button on the title line, with its icon over the time it replaces.
const ROW_ACTIONS_BUTTON_INSET_IN_PX = { right: 10, top: 6 };

interface MessagingChannelListProps {
  channels: MessagingChannel[];
  hasLoadFailed: boolean;
  hasMore: boolean;
  // True only for the first page, so a background refetch does not flash the loader.
  isLoading: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => Promise<unknown>;
  onRetryLoad: () => void;
  onSelectChannel: (channel: MessagingChannel) => void;
  searchQuery: string;
  selectedChannelId: Nullable<string>;
  viewerUserId: Nullable<string>;
}

export default function MessagingChannelList({
  channels,
  hasLoadFailed,
  hasMore,
  isLoading,
  isLoadingMore,
  onLoadMore,
  onRetryLoad,
  onSelectChannel,
  searchQuery,
  selectedChannelId,
  viewerUserId,
}: MessagingChannelListProps) {
  const theme = useTheme();
  const { draftFor } = useMessagingDraft();
  const infiniteScroll = useInfiniteScroll({ rootMargin: '200px' });
  const rowRefs = useRef(new Map<string, HTMLButtonElement>());
  const { observeRow, onScreenRowIds } = useOnScreenRowIds();
  const rowRefCallbacks = useRef(
    new Map<string, (node: Nullable<HTMLButtonElement>) => void>(),
  );
  const [focusedChannelId, setFocusedChannelId] = useState<Nullable<string>>(null);

  const focusRow = useCallback(
    (index: number) => {
      const channel = channels[Math.max(0, Math.min(index, channels.length - 1))];
      if (channel) {
        rowRefs.current.get(channel.id)?.focus();
      }
    },
    [channels],
  );

  // Stable per row, because an inline ref is detached and reattached on every render.
  const rowRefFor = useCallback(
    (channelId: string) => {
      const existingCallback = rowRefCallbacks.current.get(channelId);
      if (existingCallback) return existingCallback;

      const rowRef = (node: Nullable<HTMLButtonElement>) => {
        if (node) {
          rowRefs.current.set(channelId, node);
        } else {
          rowRefs.current.delete(channelId);
        }
        observeRow(channelId, node);
      };
      rowRefCallbacks.current.set(channelId, rowRef);

      return rowRef;
    },
    [observeRow],
  );

  useEffect(() => {
    const renderedChannelIds = new Set(channels.map((channel) => channel.id));
    rowRefCallbacks.current.forEach((_, channelId) => {
      if (!renderedChannelIds.has(channelId)) rowRefCallbacks.current.delete(channelId);
    });
  }, [channels]);

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      const targetIndexByKey: Record<string, number> = {
        ArrowDown: index + 1,
        ArrowUp: index - 1,
        End: channels.length - 1,
        Home: 0,
      };
      const hasModifier =
        event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
      const targetIndex = targetIndexByKey[event.key];
      if (hasModifier || targetIndex === undefined) return;

      event.preventDefault();
      focusRow(targetIndex);
    },
    [channels.length, focusRow],
  );

  useEffect(() => {
    if (!infiniteScroll.endReached || !hasMore || isLoadingMore) return;

    // Re-arm only once the page lands: the observer fires on a crossing, not a state.
    void onLoadMore().finally(infiniteScroll.notify);
  }, [
    hasMore,
    infiniteScroll.endReached,
    infiniteScroll.notify,
    isLoadingMore,
    onLoadMore,
  ]);

  if (isLoading) {
    return <MessagingChannelListSkeleton />;
  }

  if (hasLoadFailed) {
    return (
      <Box sx={{ px: 2, py: 4 }}>
        <MessagingLoadError
          message={t('messaging.load_channels_failed')}
          onRetry={onRetryLoad}
        />
      </Box>
    );
  }

  if (!channels.length) {
    return (
      <EmptyStateMessage
        message={
          searchQuery.trim()
            ? t('messaging.no_matching_channels')
            : t('messaging.no_channels')
        }
      />
    );
  }

  const isLoadedChannel = (channelId: Nullable<string>) =>
    channels.some((channel) => channel.id === channelId);
  const tabbableChannelId =
    [focusedChannelId, selectedChannelId].find(isLoadedChannel) ?? channels[0].id;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        onBlur={(event) => {
          const isFocusLeavingList = !event.currentTarget.contains(event.relatedTarget);
          if (isFocusLeavingList) {
            setFocusedChannelId(null);
          }
        }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        {channels.map((channel, index) => {
          const isSelected = channel.id === selectedChannelId;
          const isBroadcast = channel.channelType === MessagingChannelType.BROADCAST;
          const hasUnreadMessages = channel.unreadCount > 0;
          const hasUnmutedUnreadMessages = hasUnreadMessages && !channel.muted;
          const tags = messagingChannelTags(channel);
          // The open conversation's own composer shows its draft, so its row keeps the last message.
          const draftPreview = isSelected
            ? ''
            : draftFor(channel.id).replace(/\s+/g, ' ').trim();

          return (
            <MessagingChannelRowActions
              buttonInset={ROW_ACTIONS_BUTTON_INSET_IN_PX}
              channel={channel}
              isTabbable={channel.id === tabbableChannelId}
              key={channel.id}
            >
              <ButtonBase
                data-channel-id={channel.id}
                data-test-id="messaging-channel-row"
                onClick={() => onSelectChannel(channel)}
                onFocus={() => setFocusedChannelId(channel.id)}
                onKeyDown={(event) => handleRowKeyDown(event, index)}
                ref={rowRefFor(channel.id)}
                tabIndex={channel.id === tabbableChannelId ? 0 : -1}
                sx={{
                  alignItems: 'flex-start',
                  backgroundColor: isSelected ? theme.semantic.surfaceOverlay : undefined,
                  borderBottom: `solid 1px ${theme.semantic.divider}`,
                  display: 'flex',
                  gap: 1.25,
                  px: 2,
                  py: ROW_PADDING_Y,
                  textAlign: 'left',
                  width: '100%',
                  ...(isBroadcast
                    ? {
                        borderLeft: `solid ${BROADCAST_ACCENT_WIDTH_IN_PX}px ${theme.brandV2.colors.treadOrangeDark}`,
                        pl: `calc(${theme.spacing(2)} - ${BROADCAST_ACCENT_WIDTH_IN_PX}px)`,
                      }
                    : {}),
                  // Layered as a gradient so hover does not replace the selected row's fill.
                  '&:hover': {
                    backgroundImage: `linear-gradient(${theme.semantic.listItemHoverBg}, ${theme.semantic.listItemHoverBg})`,
                  },
                  '&.Mui-focusVisible': {
                    outline: `solid 2px ${theme.brandV2.colors.treadBlue}`,
                    outlineOffset: '-2px',
                  },
                }}
              >
                {hasUnmutedUnreadMessages && (
                  <Box
                    aria-hidden
                    data-test-id="messaging-channel-row-unread-dot"
                    sx={{
                      backgroundColor: theme.brandV2.colors.treadRed,
                      borderRadius: '50%',
                      height: UNREAD_DOT_SIZE_IN_PX,
                      left: UNREAD_DOT_LEFT_IN_PX,
                      position: 'absolute',
                      top: `calc(${theme.spacing(ROW_PADDING_Y)} + ${MESSAGING_AVATAR_SIZE_IN_PX / 2}px)`,
                      transform: 'translateY(-50%)',
                      width: UNREAD_DOT_SIZE_IN_PX,
                    }}
                  />
                )}

                <MessagingChannelAvatar channel={channel} viewerUserId={viewerUserId} />

                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                    <Typography
                      noWrap
                      sx={{
                        color: hasUnreadMessages
                          ? theme.brandV2.colors.treadBlack
                          : theme.brandV2.colors.treadGray2,
                        fontWeight: hasUnreadMessages
                          ? UNREAD_EMPHASIS_FONT_WEIGHT
                          : undefined,
                      }}
                    >
                      {messagingChannelTitle(channel)}
                    </Typography>

                    <Typography
                      {...ROW_DETAIL_REPLACED_BY_ACTIONS}
                      data-test-id="messaging-channel-row-time"
                      sx={{
                        color: hasUnreadMessages
                          ? theme.brandV2.colors.treadGray2
                          : theme.brandV2.colors.treadGray4,
                        flexShrink: 0,
                        fontWeight: hasUnreadMessages
                          ? MESSAGING_LABEL_FONT_WEIGHT
                          : undefined,
                      }}
                      variant="body2"
                    >
                      {messagingLastActivityLabel(channel.lastMessageAt)}
                    </Typography>
                  </Box>

                  <ChannelRowSummaryLine
                    channel={channel}
                    draftPreview={draftPreview}
                    isOnScreen={onScreenRowIds.has(channel.id)}
                    viewerUserId={viewerUserId}
                  />

                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {channel.isArchived && <MessagingArchivedTag />}
                      {tags.map((tag, index) => (
                        <MessagingTag
                          key={tag}
                          label={tag}
                          tone={isBroadcast && index === 0 ? 'emphasized' : 'neutral'}
                        />
                      ))}
                    </Box>

                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexShrink: 0,
                        gap: 0.5,
                        ml: 'auto',
                      }}
                    >
                      {channel.muted && <MutedConversationIcon />}
                      <UnreadCountBadge
                        count={channel.unreadCount}
                        isMuted={channel.muted}
                      />
                    </Box>
                  </Box>
                </Box>
              </ButtonBase>
            </MessagingChannelRowActions>
          );
        })}
      </Box>

      <Box ref={infiniteScroll.nodeRef} />

      {isLoadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
}

function MessagingChannelListSkeleton() {
  const theme = useTheme();

  return (
    <Box
      data-test-id="messaging-channel-list-skeleton"
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <Box
          key={index}
          sx={{
            alignItems: 'center',
            borderBottom: `solid 1px ${theme.semantic.divider}`,
            display: 'flex',
            gap: 1.25,
            px: 2,
            py: ROW_PADDING_Y,
          }}
        >
          <Skeleton
            height={MESSAGING_AVATAR_SIZE_IN_PX}
            sx={{ borderRadius: '50%', flexShrink: 0 }}
            variant="circular"
            width={MESSAGING_AVATAR_SIZE_IN_PX}
          />

          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 0.75 }}>
            <Skeleton height={14} variant="rectangular" width="55%" />
            <Skeleton height={12} variant="rectangular" width="80%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ChannelRowSummaryLine({
  channel,
  draftPreview,
  isOnScreen,
  viewerUserId,
}: {
  channel: MessagingChannel;
  draftPreview: string;
  isOnScreen: boolean;
  viewerUserId: Nullable<string>;
}) {
  const theme = useTheme();
  const typers = useMessagingChannelRowTypers({ channel, isOnScreen, viewerUserId });

  if (typers.length > 0) {
    return (
      <Typography
        data-test-id="messaging-channel-row-typing"
        noWrap
        sx={{ color: theme.semantic.textSecondary }}
        variant="body2"
      >
        {messagingTypingLabel(typers)}
      </Typography>
    );
  }

  if (draftPreview) {
    return (
      <Typography
        data-test-id="messaging-channel-row-draft"
        noWrap
        sx={{ color: theme.semantic.textSecondary }}
        variant="body2"
      >
        <Box
          component="span"
          sx={{
            color: theme.brandV2.colors.treadOrangeDark,
            fontWeight: MESSAGING_LABEL_FONT_WEIGHT,
          }}
        >
          {t('messaging.preview.draft')}
        </Box>{' '}
        {draftPreview}
      </Typography>
    );
  }

  if (!channel.lastMessage) return null;

  return (
    <Typography
      data-test-id="messaging-channel-row-preview"
      noWrap
      sx={{
        color: theme.semantic.textSecondary,
        ...(channel.lastMessage.isDeleted ? { fontStyle: 'italic' } : {}),
      }}
      variant="body2"
    >
      {messagingChannelPreview(channel.lastMessage, viewerUserId, {
        shouldNameSender: true,
      })}
    </Typography>
  );
}
